/* eslint-disable jsx-a11y/anchor-is-valid */
import $ from 'jquery';
import React, { useCallback, useMemo, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { chain, isEmpty, noop, result } from 'underscore';
import { KeyCodes } from '@fluentui/react';
import { DeliveryStatusPopupFactory } from '@bingads-webui-campaign/delivery-status-popup-factory';
import { buildLegacyI18n } from '@bingads-webui/build-legacy-i18n';
import { withDefaultStyles } from '@bingads-webui-react/with-default-styles';

import { DeliveryStatus, WhitelistDeliveryStatus } from '../constants/delivery-status';
import { legacyI18nKeys } from '../constants/legacy-i18n';
import { getAudienceStatusStyle } from '../styles/audience-status';

const UnstyledAudienceStatus = (props) => {
  const {
    audienceGroup: { Status, Dimensions },
    classes,
    i18n,
  } = props;

  const DeliveryStatusDetails = useMemo(
    () => chain(Dimensions)
      .filter(dimension => dimension.Type === 'Audience')
      .map(dimension => result(dimension, 'Audiences'))
      .flatten()
      .map(audience => result(audience, 'AudienceTrackingStatus'))
      .compact()
      .uniq()
      .filter(status => !WhitelistDeliveryStatus.includes(status))
      .value(),
    [Dimensions]
  );

  const entityForDeliveryStatusPopupFactory = useMemo(() => ({
    grid: {
      gridView: {
        keyOfElement: noop,
        itemWithKey: () => ({ DeliveryStatusDetails }),
      },
    },
  }), [DeliveryStatusDetails]);

  const legacyI18n = useMemo(() => buildLegacyI18n(i18n, legacyI18nKeys), [i18n]);

  const containerRef = useRef(null);

  const showDeliveryStatusDetails = useCallback((event) => {
    const view = new DeliveryStatusPopupFactory({
      i18n: legacyI18n,
      ...entityForDeliveryStatusPopupFactory,
    });
    view.showRejectionPopup({
      event,
      view: $(containerRef.current),
      popoverId: 'delivery-status-popup-callout',
    });
    event.stopPropagation();
  }, [entityForDeliveryStatusPopupFactory, legacyI18n]);

  const showDeliveryStatusDetailsByKey = useCallback((event) => {
    if (event.keyCode === KeyCodes.enter && event.currentTarget === event.target) {
      showDeliveryStatusDetails(event);
    }
  }, [showDeliveryStatusDetails]);

  const hideDeliveryStatusDetails = useCallback((event) => {
    if ($(event.relatedTarget).closest('#delivery-status-popup-callout').length === 0) {
      $(containerRef.current).trigger('closeDeliveryStatusPopup');
    }
  }, []);

  return (
    <div
      className={classNames(['audience-delivery-status', classes.statusContainer])}
      onMouseLeave={hideDeliveryStatusDetails}
      ref={containerRef}
    >
      {isEmpty(DeliveryStatusDetails) && i18n.getString(DeliveryStatus[Status] || '')}
      {!isEmpty(DeliveryStatusDetails) && (
        <span
          className="underline"
          role="button"
          tabIndex="0"
          onClick={showDeliveryStatusDetails}
          onKeyDown={showDeliveryStatusDetailsByKey}
          onMouseEnter={showDeliveryStatusDetails}
        >
          {i18n.getString(DeliveryStatus[Status] || '')}
        </span>
      )}
    </div>
  );
};

UnstyledAudienceStatus.propTypes = {
  audienceGroup: PropTypes.shape({
    Status: PropTypes.string.isRequired,
    Dimensions: PropTypes.arrayOf(PropTypes.shape({
      Type: PropTypes.string.isRequired,
    })).isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    statusContainer: PropTypes.string.isRequired,
  }).isRequired,
  i18n: PropTypes.shape({
    getString: PropTypes.func,
  }).isRequired,
};

export const AudienceStatus = withDefaultStyles(UnstyledAudienceStatus, getAudienceStatusStyle);
